import { useState, useCallback, FormEvent } from 'react';
import { authService } from '../services/authService';
import { LOGIN_MODES } from '../shared/constants';
import { validateEmail, validatePassword, validatePhone, validateOTP, validateResetEmail, formatPhoneNumber } from '../shared/utils';

interface FormData {
  email: string;
  password: string;
  emailWithOtp: string;
  otp: string;
  resetEmail: string;
}

interface AuthErrors {
  email?: string;
  password?: string;
  emailWithOtp?: string;
  otp?: string;
  resetEmail?: string;
  general?: string;
  verificationCode?: string;
  confirmPassword?: string;
}

interface AuthResponse {
  success: boolean;
  data?: any;
  error?: string;
  errors?: AuthErrors;
  message?: string;
  type?: string;
}

const initialFormData: FormData = {
  email: '',
  password: '',
  emailWithOtp: '',
  otp: '',
  resetEmail: ''
};

const isValidPassword = (password: string): boolean => {
  const minLength = 8;
  const hasNumber = /\d/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  return password.length >= minLength && hasNumber && hasLowerCase && hasUpperCase && hasSpecialChar;
};

export const useAuthLogic = () => {
  const [loginMode, setLoginMode] = useState(LOGIN_MODES.EMAIL);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<AuthErrors>({});
  const [otpSent, setOtpSent] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');

  const clearFieldError = useCallback((field: keyof AuthErrors) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const updateFormData = useCallback((field: keyof FormData, value: string) => {
    const processedValue = field === 'otp' ? value.replace(/\D/g, '').slice(0, 6) : value;
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    clearFieldError(field as keyof AuthErrors);
  }, [clearFieldError]);

  const validateCurrentForm = useCallback((): AuthErrors => {
    const newErrors: AuthErrors = {};

    switch (loginMode) {
      case LOGIN_MODES.EMAIL:
        const emailError = validateEmail(formData.email);
        if (emailError) newErrors.email = emailError;
        const passwordError = validatePassword(formData.password);
        if (passwordError) newErrors.password = passwordError;
        break;

      case LOGIN_MODES.EMAILWITHOTP:
        if (!otpSent) {
          const emailError = validateEmail(formData.emailWithOtp);
          if (emailError) newErrors.email = emailError;
        } else {
          const otpError = validateOTP(formData.otp);
          if (otpError) newErrors.otp = otpError;
        }
        break;

      case LOGIN_MODES.FORGOT_PASSWORD:
        const resetEmailError = validateResetEmail(formData.resetEmail);
        if (resetEmailError) newErrors.resetEmail = resetEmailError;
        break;

      default:
        newErrors.general = 'Invalid login mode';
    }

    return newErrors;
  }, [loginMode, formData, otpSent]);

  const validateForm = useCallback((): boolean => {
    const newErrors = validateCurrentForm();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [validateCurrentForm]);

  const setLoadingAndClearErrors = useCallback((loading: boolean) => {
    setIsLoading(loading);
    if (loading) setErrors({});
  }, []);

  const handleGenerateOTP = useCallback(async (event?: FormEvent): Promise<AuthResponse> => {
    if (event) event.preventDefault();

    const validationErrors: AuthErrors = {};
    if (!formData.emailWithOtp.trim()) {
      validationErrors.emailWithOtp = 'Email ID is required';
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return { success: false, errors: validationErrors };
    }

    setLoadingAndClearErrors(true);

    try {
      const result = await authService.sendEmailOTP(formData.emailWithOtp);
      setSessionId(result.sessionId || '');
      setOtpSent(true);
      return { success: true, message: result.message, type: 'otp_sent' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send OTP';
      setErrors({ general: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [formData.emailWithOtp, setLoadingAndClearErrors]);

  const handleVerifyOTP = useCallback(async (event?: FormEvent): Promise<AuthResponse> => {
    if (event) event.preventDefault();

    const validationErrors: AuthErrors = {};
    if (!formData.otp.trim()) {
      validationErrors.otp = 'OTP is required';
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return { success: false, errors: validationErrors };
    }

    setLoadingAndClearErrors(true);

    try {
      const result = await authService.verifyEmailOTP(formData.emailWithOtp, formData.otp, sessionId);
      return { success: true, data: result, type: 'login' };
    } catch (error: any) {
      const errorMessage = error.message || 'OTP verification failed';
      setErrors({ general: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [formData.emailWithOtp, formData.otp, sessionId, setLoadingAndClearErrors]);

  const handleVerifyAccount = useCallback(async (event?: FormEvent): Promise<AuthResponse> => {
    if (event) event.preventDefault();

    const validationErrors: AuthErrors = {};
    if (!formData.resetEmail.trim()) {
      validationErrors.resetEmail = 'Email is required';
    } else if (validateEmail(formData.resetEmail)) {
      validationErrors.resetEmail = 'Invalid email format';
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return { success: false, errors: validationErrors };
    }

    setLoadingAndClearErrors(true);

    try {
      const result = await authService.handleForgotPassword(formData.resetEmail);
      return { success: true, message: result.message, type: 'verification_sent' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send verification code';
      setErrors({ general: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [formData.resetEmail, setLoadingAndClearErrors]);

  const handlePasswordResetConfirm = useCallback(async (
    verificationCode: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<AuthResponse> => {
    setIsLoading(true);

    try {
      const validationErrors: AuthErrors = {};

      if (!verificationCode.trim()) {
        validationErrors.verificationCode = 'Enter the verification code you received in email';
      }

      if (!newPassword.trim()) {
        validationErrors.password = 'New password is required';
      } else if (!isValidPassword(newPassword.trim())) {
        validationErrors.password = 'New password must contain at least 1 number, 1 lowercase char, 1 uppercase char, 1 special char and length should be minimum 8 char';
      }

      if (confirmPassword !== newPassword) {
        validationErrors.confirmPassword = 'Passwords do not match';
      }

      if (Object.keys(validationErrors).length) {
        return { success: false, errors: validationErrors };
      }

      await authService.confirmPasswordReset(formData.resetEmail, verificationCode, newPassword);
      return { success: true, message: 'Password reset successfully' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reset password';
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [formData.resetEmail]);

 const handleSubmit = useCallback(async (event?: FormEvent): Promise<AuthResponse> => {
  if (event) event.preventDefault();

  if (!validateForm()) {
    return { success: false, errors };
  }

  setLoadingAndClearErrors(true);

  try {
    switch (loginMode) {
      case LOGIN_MODES.EMAIL:
        const result = await authService.loginWithEmail(formData.email, formData.password);
        return { success: true, data: result, type: 'login' };

      case LOGIN_MODES.EMAILWITHOTP:
        return otpSent ? await handleVerifyOTP() : await handleGenerateOTP();

      case LOGIN_MODES.FORGOT_PASSWORD:
        await authService.sendPasswordReset(formData.resetEmail);
        setResetEmailSent(true);
        return { success: true, type: 'reset_sent' };

      default:
        throw new Error('Invalid login mode');
    }
  } catch (error: any) {
    const errorMessage = error.message || 'An unexpected error occurred';
    setErrors({ general: errorMessage });
    return { success: false, error: errorMessage };
  } finally {
    setIsLoading(false);
  }
}, [validateForm, loginMode, formData, otpSent, errors, handleGenerateOTP, handleVerifyOTP, setLoadingAndClearErrors]);
  const resetState = useCallback(() => {
    setOtpSent(false);
    setResetEmailSent(false);
    setErrors({});
    setFormData(initialFormData);
    setShowPassword(false);
  }, []);

  const changeLoginMode = useCallback((mode: any) => {
    setLoginMode(mode);
    resetState();
  }, [resetState]);

  const goToForgotPassword = useCallback(() => {
    changeLoginMode(LOGIN_MODES.FORGOT_PASSWORD);
  }, [changeLoginMode]);

  const backToLogin = useCallback(() => {
    changeLoginMode(LOGIN_MODES.EMAIL);
  }, [changeLoginMode]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const resetAuth = useCallback(() => {
    setLoginMode(LOGIN_MODES.EMAIL);
    resetState();
    setIsLoading(false);
  }, [resetState]);

  return {
    loginMode,
    showPassword,
    formData,
    errors,
    otpSent,
    resetEmailSent,
    isLoading,
    sessionId,
    updateFormData,
    handleSubmit,
    changeLoginMode,
    goToForgotPassword,
    backToLogin,
    togglePasswordVisibility,
    resetAuth,
    handleVerifyAccount,
    handlePasswordResetConfirm,
    handleGenerateOTP,
    handleVerifyOTP,
    LOGIN_MODES
  };
};